import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { useSalesforce } from "../contexts/SalesforceContext";
import {
  getProfilePictureInstant,
  getProfilePicture,
  preloadProfilePicture,
} from "../utils/profilePicture";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { navigationGroups } from "@/shared/sidebar/app-sidebar";

const Home: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const {
    isLoading: isSalesforceLoading,
    error: salesforceError,
    queryWithMetadata,
  } = useSalesforce();
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [profilePicture, setProfilePicture] = useState<string | null>(null);
  const [salesforceDemo, setSalesforceDemo] = useState<{
    isLoading: boolean;
    data: any[] | null;
    error: string | null;
    serviceUsed?: string;
    fallbackUsed?: boolean;
    thaiforceError?: string;
  }>({
    isLoading: false,
    data: null,
    error: null,
  });

  // Only show loading state on initial load
  useEffect(() => {
    if (!isSalesforceLoading) {
      setIsInitialLoad(false);
    }
  }, [isSalesforceLoading]);

  // Load profile picture instantly when user is available
  useEffect(() => {
    const loadProfilePicture = async () => {
      if (user?.id) {
        // First try to get it instantly from memory cache
        const instantPicture = getProfilePictureInstant(user.id);
        if (instantPicture) {
          setProfilePicture(instantPicture);
          return;
        }

        try {
          // If not in memory cache, try async load (will check localStorage and fetch if needed)
          const picture = await getProfilePicture(user.id, user.name);
          setProfilePicture(picture);
        } catch (error) {
          // Silent error handling
        }
      }
    };

    // Only load profile picture after initial load is complete and user is authenticated
    if (isAuthenticated && user && !isInitialLoad) {
      loadProfilePicture();
    }
  }, [isAuthenticated, user, isInitialLoad]);

  // Demo Salesforce query - automatically lookup user's contact
  useEffect(() => {
    const lookupUserContact = async () => {
      if (!user?.email) return;

      setSalesforceDemo({ isLoading: true, data: null, error: null });
      try {
        // Query to find contact matching the logged-in user's email
        const response = await queryWithMetadata(
          `SELECT Id, Name, Email, Phone, Title, Account.Name FROM Contact WHERE Email = '${user.email}' LIMIT 1`
        );
        setSalesforceDemo({
          isLoading: false,
          data: response.records,
          error: null,
          serviceUsed: response.serviceUsed,
          fallbackUsed: response.fallbackUsed,
          thaiforceError: response.thaiforceError,
        });
      } catch (error) {
        setSalesforceDemo({
          isLoading: false,
          data: null,
          error:
            error instanceof Error
              ? error.message
              : "Failed to query Salesforce",
        });
      }
    };

    // Only run the lookup if user is authenticated and we have user data
    if (isAuthenticated && user?.email && !isInitialLoad) {
      lookupUserContact();
    }
  }, [isAuthenticated, user, isInitialLoad, queryWithMetadata]);

  // if (!isAuthenticated) {
  //   return (
  //     <div className="min-h-screen bg-gray-50">
  //       <div className="max-w-4xl mx-auto pt-16 px-4 sm:px-6 lg:px-8">
  //         <div className="bg-white rounded-lg shadow-sm p-8 my-8">
  //           <h1 className="text-2xl font-semibold text-gray-800 mb-6">
  //             Azure AD Authentication Setup Required
  //           </h1>

  //           <div className="bg-blue-50 border border-blue-200 rounded-md p-6 mb-6">
  //             <div className="flex items-start">
  //               <svg
  //                 className="w-6 h-6 text-blue-600 mr-3 mt-0.5 flex-shrink-0"
  //                 fill="currentColor"
  //                 viewBox="0 0 20 20"
  //               >
  //                 <path
  //                   fillRule="evenodd"
  //                   d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
  //                   clipRule="evenodd"
  //                 />
  //               </svg>
  //               <div>
  //                 <h3 className="text-blue-800 font-medium mb-2">
  //                   Authentication Not Configured
  //                 </h3>
  //                 <p className="text-blue-700 text-sm">
  //                   To use this application, you need to set up Microsoft Azure
  //                   AD authentication. Follow the steps below to configure your
  //                   Azure app registration.
  //                 </p>
  //               </div>
  //             </div>
  //           </div>

  //           <div className="space-y-6">
  //             <div>
  //               <h2 className="text-lg font-medium text-gray-800 mb-4">
  //                 Setup Instructions
  //               </h2>

  //               <div className="space-y-4">
  //                 <div className="border border-gray-200 rounded-lg p-4">
  //                   <div className="flex items-start">
  //                     <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
  //                       <span className="text-blue-600 font-semibold text-sm">
  //                         1
  //                       </span>
  //                     </div>
  //                     <div className="flex-1">
  //                       <h3 className="font-medium text-gray-800 mb-2">
  //                         Create Azure App Registration
  //                       </h3>
  //                       <ul className="text-gray-600 text-sm space-y-1">
  //                         <li>
  //                           • Open{" "}
  //                           <a
  //                             href="https://portal.azure.com"
  //                             target="_blank"
  //                             rel="noopener noreferrer"
  //                             className="text-blue-600 hover:underline"
  //                           >
  //                             portal.azure.com
  //                           </a>
  //                         </li>
  //                         <li>
  //                           • Navigate to "Azure Active Directory" → "App
  //                           registrations"
  //                         </li>
  //                         <li>• Click "New registration"</li>
  //                         <li>
  //                           • Choose{" "}
  //                           <strong>Single-page application (SPA)</strong> as
  //                           the platform
  //                         </li>
  //                       </ul>
  //                     </div>
  //                   </div>
  //                 </div>

  //                 <div className="border border-gray-200 rounded-lg p-4">
  //                   <div className="flex items-start">
  //                     <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
  //                       <span className="text-blue-600 font-semibold text-sm">
  //                         2
  //                       </span>
  //                     </div>
  //                     <div className="flex-1">
  //                       <h3 className="font-medium text-gray-800 mb-2">
  //                         Configure Redirect URIs
  //                       </h3>
  //                       <p className="text-gray-600 text-sm mb-2">
  //                         Add these redirect URIs to your SPA configuration:
  //                       </p>
  //                       <div className="bg-gray-100 p-3 rounded text-sm font-mono space-y-1">
  //                         <div>http://localhost:3000</div>
  //                         <div>https://&#123;appname&#125;.duo.tax</div>
  //                       </div>
  //                       <p className="text-gray-500 text-xs mt-2">
  //                         Replace &#123;appname&#125; with your actual
  //                         application name
  //                       </p>
  //                     </div>
  //                   </div>
  //                 </div>

  //                 <div className="border border-gray-200 rounded-lg p-4">
  //                   <div className="flex items-start">
  //                     <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
  //                       <span className="text-blue-600 font-semibold text-sm">
  //                         3
  //                       </span>
  //                     </div>
  //                     <div className="flex-1">
  //                       <h3 className="font-medium text-gray-800 mb-2">
  //                         Configure API Permissions
  //                       </h3>
  //                       <p className="text-gray-600 text-sm mb-3">
  //                         Add these Microsoft Graph permissions and grant admin
  //                         consent:
  //                       </p>

  //                       <div className="overflow-x-auto">
  //                         <table className="min-w-full text-sm">
  //                           <thead>
  //                             <tr className="border-b border-gray-200">
  //                               <th className="text-left text-gray-600 font-medium py-2 px-3">
  //                                 Permission
  //                               </th>
  //                               <th className="text-left text-gray-600 font-medium py-2 px-3">
  //                                 Type
  //                               </th>
  //                               <th className="text-left text-gray-600 font-medium py-2 px-3">
  //                                 Admin Consent
  //                               </th>
  //                             </tr>
  //                           </thead>
  //                           <tbody className="space-y-1">
  //                             <tr className="border-b border-gray-100">
  //                               <td className="py-2 px-3 font-mono text-xs">
  //                                 email
  //                               </td>
  //                               <td className="py-2 px-3 text-gray-600">
  //                                 Delegated
  //                               </td>
  //                               <td className="py-2 px-3 text-gray-600">No</td>
  //                             </tr>
  //                             <tr className="border-b border-gray-100">
  //                               <td className="py-2 px-3 font-mono text-xs">
  //                                 openid
  //                               </td>
  //                               <td className="py-2 px-3 text-gray-600">
  //                                 Delegated
  //                               </td>
  //                               <td className="py-2 px-3 text-gray-600">No</td>
  //                             </tr>
  //                             <tr className="border-b border-gray-100">
  //                               <td className="py-2 px-3 font-mono text-xs">
  //                                 profile
  //                               </td>
  //                               <td className="py-2 px-3 text-gray-600">
  //                                 Delegated
  //                               </td>
  //                               <td className="py-2 px-3 text-gray-600">No</td>
  //                             </tr>
  //                             <tr className="border-b border-gray-100">
  //                               <td className="py-2 px-3 font-mono text-xs">
  //                                 ProfilePhoto.Read.All
  //                               </td>
  //                               <td className="py-2 px-3 text-gray-600">
  //                                 Delegated
  //                               </td>
  //                               <td className="py-2 px-3 text-yellow-600">
  //                                 Yes
  //                               </td>
  //                             </tr>
  //                             <tr className="border-b border-gray-100">
  //                               <td className="py-2 px-3 font-mono text-xs">
  //                                 User.Read
  //                               </td>
  //                               <td className="py-2 px-3 text-gray-600">
  //                                 Delegated
  //                               </td>
  //                               <td className="py-2 px-3 text-gray-600">No</td>
  //                             </tr>
  //                             <tr className="border-b border-gray-100">
  //                               <td className="py-2 px-3 font-mono text-xs">
  //                                 User.Read.All
  //                               </td>
  //                               <td className="py-2 px-3 text-gray-600">
  //                                 Delegated
  //                               </td>
  //                               <td className="py-2 px-3 text-yellow-600">
  //                                 Yes
  //                               </td>
  //                             </tr>
  //                             <tr>
  //                               <td className="py-2 px-3 font-mono text-xs">
  //                                 User.ReadBasic.All
  //                               </td>
  //                               <td className="py-2 px-3 text-gray-600">
  //                                 Delegated
  //                               </td>
  //                               <td className="py-2 px-3 text-gray-600">No</td>
  //                             </tr>
  //                           </tbody>
  //                         </table>
  //                       </div>

  //                       <div className="mt-3 p-3 bg-yellow-50 rounded border border-yellow-200">
  //                         <div className="flex items-start">
  //                           <svg
  //                             className="w-4 h-4 text-yellow-600 mr-2 mt-0.5 flex-shrink-0"
  //                             fill="currentColor"
  //                             viewBox="0 0 20 20"
  //                           >
  //                             <path
  //                               fillRule="evenodd"
  //                               d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
  //                               clipRule="evenodd"
  //                             />
  //                           </svg>
  //                           <div>
  //                             <p className="text-yellow-800 text-sm font-medium">
  //                               Important
  //                             </p>
  //                             <p className="text-yellow-700 text-sm">
  //                               After adding permissions, click "Grant admin
  //                               consent" for your organization to enable profile
  //                               photo access and user reading capabilities.
  //                             </p>
  //                           </div>
  //                         </div>
  //                       </div>
  //                     </div>
  //                   </div>
  //                 </div>

  //                 <div className="border border-gray-200 rounded-lg p-4">
  //                   <div className="flex items-start">
  //                     <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
  //                       <span className="text-blue-600 font-semibold text-sm">
  //                         4
  //                       </span>
  //                     </div>
  //                     <div className="flex-1">
  //                       <h3 className="font-medium text-gray-800 mb-2">
  //                         Update Frontend Environment Configuration
  //                       </h3>
  //                       <p className="text-gray-600 text-sm mb-2">
  //                         Create/update your frontend{" "}
  //                         <code className="bg-gray-200 px-1 rounded">.env</code>{" "}
  //                         file with these variables:
  //                       </p>

  //                       <div className="mb-3">
  //                         <p className="text-gray-700 text-sm font-medium mb-1">
  //                           Azure Authentication:
  //                         </p>
  //                         <div className="bg-gray-100 p-3 rounded text-sm font-mono space-y-1">
  //                           <div>VITE_AZURE_CLIENT_ID=your-client-id-here</div>
  //                           <div>
  //                             VITE_AZURE_TENANT_ID=79b4768a-6997-41b1-a1e6-39771c85b325
  //                           </div>
  //                         </div>
  //                       </div>

  //                       <div>
  //                         <p className="text-gray-700 text-sm font-medium mb-1">
  //                           Backend API Configuration:
  //                         </p>
  //                         <div className="bg-gray-100 p-3 rounded text-sm font-mono space-y-1">
  //                           <div className="text-gray-600">
  //                             # For Development:
  //                           </div>
  //                           <div>VITE_BACKEND_URL=http://localhost:3001</div>
  //                           <div className="text-gray-600">
  //                             # For Production:
  //                           </div>
  //                           <div>
  //                             VITE_BACKEND_URL=https://api.&#123;your-app-name&#125;.duo.tax
  //                           </div>
  //                         </div>
  //                       </div>

  //                       <div className="mt-3 text-gray-500 text-xs space-y-1">
  //                         <p>
  //                           • Replace "your-client-id-here" with your actual
  //                           Application (client) ID from Azure
  //                         </p>
  //                         <p>
  //                           • Replace "&#123;your-app-name&#125;" with your
  //                           application name for production URL
  //                         </p>
  //                         <p>
  //                           • Use localhost URL for development, duo.tax URL for
  //                           production deployment
  //                         </p>
  //                       </div>
  //                     </div>
  //                   </div>
  //                 </div>

  //                 <div className="border border-gray-200 rounded-lg p-4">
  //                   <div className="flex items-start">
  //                     <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
  //                       <span className="text-blue-600 font-semibold text-sm">
  //                         5
  //                       </span>
  //                     </div>
  //                     <div className="flex-1">
  //                       <h3 className="font-medium text-gray-800 mb-2">
  //                         Configure Salesforce Backend Integration
  //                       </h3>
  //                       <p className="text-gray-600 text-sm mb-2">
  //                         Create/update your backend{" "}
  //                         <code className="bg-gray-200 px-1 rounded">.env</code>{" "}
  //                         file with these variables:
  //                       </p>

  //                       <div className="mb-3">
  //                         <p className="text-gray-700 text-sm font-medium mb-1">
  //                           Server Configuration:
  //                         </p>
  //                         <div className="bg-gray-100 p-3 rounded text-sm font-mono space-y-1">
  //                           <div>NODE_ENV=development</div>
  //                           <div>PORT=3001</div>
  //                         </div>
  //                       </div>

  //                       <div>
  //                         <p className="text-gray-700 text-sm font-medium mb-1">
  //                           Salesforce API Credentials:
  //                         </p>
  //                         <div className="bg-gray-100 p-3 rounded text-sm font-mono space-y-1">
  //                           <div>SALESFORCE_CONSUMER_KEY=your-consumer-key</div>
  //                           <div>
  //                             SALESFORCE_CONSUMER_SECRET=your-consumer-secret
  //                           </div>
  //                           <div>
  //                             SALESFORCE_REFRESH_TOKEN=your-refresh-token
  //                           </div>
  //                         </div>
  //                       </div>

  //                       <div className="mt-3 p-3 bg-blue-50 rounded border border-blue-200">
  //                         <div className="flex items-start">
  //                           <svg
  //                             className="w-4 h-4 text-blue-600 mr-2 mt-0.5 flex-shrink-0"
  //                             fill="currentColor"
  //                             viewBox="0 0 20 20"
  //                           >
  //                             <path
  //                               fillRule="evenodd"
  //                               d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
  //                               clipRule="evenodd"
  //                             />
  //                           </svg>
  //                           <div className="flex-1">
  //                             <p className="text-blue-800 text-sm font-medium mb-2">
  //                               Salesforce Token Setup
  //                             </p>
  //                             <p className="text-blue-700 text-sm mb-3">
  //                               Get your Salesforce Consumer Key, Consumer
  //                               Secret, and Refresh Token:
  //                             </p>
  //                             <a
  //                               href="https://token.duo.tax"
  //                               target="_blank"
  //                               rel="noopener noreferrer"
  //                               className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors duration-200"
  //                             >
  //                               <svg
  //                                 className="w-4 h-4 mr-2"
  //                                 fill="currentColor"
  //                                 viewBox="0 0 20 20"
  //                               >
  //                                 <path
  //                                   fillRule="evenodd"
  //                                   d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
  //                                   clipRule="evenodd"
  //                                 />
  //                               </svg>
  //                               Get Salesforce credentials from token.duo.tax
  //                             </a>
  //                           </div>
  //                         </div>
  //                       </div>

  //                       <div className="mt-3 text-gray-500 text-xs space-y-1">
  //                         <p>
  //                           • Backend .env file should be in your server/backend
  //                           directory
  //                         </p>
  //                         <p>
  //                           • Keep Salesforce credentials secure and never
  //                           commit them to version control
  //                         </p>
  //                         <p>
  //                           • Use NODE_ENV=production for production deployments
  //                         </p>
  //                       </div>
  //                     </div>
  //                   </div>
  //                 </div>
  //               </div>
  //             </div>

  //             <div className="border-t border-gray-200 pt-6">
  //               <div className="bg-green-50 border border-green-200 rounded-md p-4">
  //                 <div className="flex items-center">
  //                   <svg
  //                     className="w-5 h-5 text-green-600 mr-2"
  //                     fill="currentColor"
  //                     viewBox="0 0 20 20"
  //                   >
  //                     <path
  //                       fillRule="evenodd"
  //                       d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
  //                       clipRule="evenodd"
  //                     />
  //                   </svg>
  //                   <span className="text-green-800 font-medium">
  //                     Once configured, refresh this page to start using the
  //                     application!
  //                   </span>
  //                 </div>
  //               </div>
  //             </div>
  //           </div>
  //         </div>
  //       </div>
  //     </div>
  //   );
  // }

  if (isInitialLoad) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="p-6 border border-gray-200 rounded bg-white shadow-sm">
          <p className="text-gray-800">Loading application...</p>
        </div>
      </div>
    );
  }

  if (salesforceError) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="p-6 border border-gray-200 rounded bg-white shadow-sm">
          <p className="text-red-600">
            Error connecting to Salesforce. Please try again later.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Dashboard</h1>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {navigationGroups
            .filter((item) => item.url !== "/")
            .map((item) => (
              <Link
                key={item.url}
                to={item.url}
                className="block transition-transform hover:scale-105"
              >
                <Card className="h-full hover:border-primary cursor-pointer">
                  <CardHeader>
                    <CardTitle className="text-xl">{item.title}</CardTitle>
                  </CardHeader>
                </Card>
              </Link>
            ))}
        </div>
      </div>
    </div>
  );
};

export default Home;
